import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, CheckCircle2, FileText, Keyboard, Loader2, PackagePlus, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarcodeScanner } from "./BarcodeScanner";
import { InstanceForm, enregistrerInstance, instanceValide, instanceVide } from "./InstanceForm";
import { api, errMsg } from "@/lib/api";
import { AIDE_AWB, TYPES_AWB, lireCode, numeroLisible } from "@/lib/colisCode";
import { ECRAN_TACTILE, useDouchette, useRafale } from "@/lib/douchette";

/**
 * « Scanner un colis » : scan (ou saisie) de l'airwaybill, puis tout de suite
 * l'instance (type, problème, date). Si le colis est déjà enregistré,
 * l'instance est ajoutée à sa fiche. « Enregistrer et scanner le suivant »
 * enchaîne les colis sans quitter la fenêtre.
 */
export function AjoutColisDialog({ open, onClose, onCreated, startScan = false, codeInitial = null }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("saisie"); // saisie | scan
  const [saisie, setSaisie] = useState("");
  const [lu, setLu] = useState(null); // { text, format } du dernier scan
  const [busy, setBusy] = useState(null);
  const [motifs, setMotifs] = useState(null);
  const [inst, setInst] = useState(instanceVide());
  const [existant, setExistant] = useState(undefined); // undefined = vérification, null = nouveau, objet = fiche
  const [compteur, setCompteur] = useState(0); // colis enregistrés dans cette série
  const verif = useRef(0);
  // Terminal / téléphone : pas de clavier à l'ouverture (le lecteur écrit sans lui).
  const [clavier, setClavier] = useState(!ECRAN_TACTILE);
  const champNumero = useRef(null);

  const reset = (scan) => {
    setMode(scan ? "scan" : "saisie"); setSaisie(""); setLu(null); setBusy(null);
    setInst(instanceVide()); setExistant(undefined);
  };
  useEffect(() => {
    if (!open) return;
    reset(startScan && !codeInitial); setCompteur(0);
    if (codeInitial) detecte({ text: codeInitial, format: "Lecteur" });
    if (!motifs) api.get("/colis/motifs-instance").then(({ data }) => setMotifs(data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const { numero, type } = lireCode(saisie);
  const valide = !!type;
  const depuisScan = lu && lireCode(lu.text).numero === numero;
  const infoLu = depuisScan ? lireCode(lu.text) : null;
  const et = infoLu?.etiquette;

  // Colis déjà enregistré ? (l'instance ira sur sa fiche)
  useEffect(() => {
    if (!valide) { setExistant(undefined); return undefined; }
    const n = ++verif.current;
    setExistant(undefined);
    const t = setTimeout(() => {
      api.get(`/colis/numero/${numero}`)
        .then(({ data }) => { if (n === verif.current) setExistant(data); })
        .catch(() => { if (n === verif.current) setExistant(null); });
    }, 200);
    return () => clearTimeout(t);
  }, [numero, valide]);

  const detecte = (r) => {
    const x = lireCode(r.text);
    setLu(r);
    setSaisie(x.numero);
    setMode("saisie");
    toast.success(`${TYPES_AWB[x.type]?.label || "Code"} : ${numeroLisible(x.numero)}`);
  };

  // Lecteur intégré (Zebra) / douchette : le n° est pris sans cliquer dans le champ
  // (et la caméra se ferme si elle était ouverte).
  useDouchette((code) => { if (!busy) detecte({ text: code, format: "Lecteur" }); }, open);
  // Lecteur qui écrit dans le champ sans Entrée : pris comme un scan dès qu'il a fini.
  const saisieRef = useRef("");
  saisieRef.current = saisie;
  const rafale = useRafale((code) => { if (!busy && code && lireCode(code).numero !== saisieRef.current) detecte({ text: code, format: "Lecteur" }); });

  /** suite : "fiche" (ouvrir la fiche) ou "suivant" (scanner le colis suivant) */
  const enregistrer = async (avecInstance, suite) => {
    setBusy("colis");
    try {
      let fiche = existant;
      if (!fiche) {
        try {
          ({ data: fiche } = await api.post("/colis", {
            numero, source: depuisScan ? "scan" : "saisie", code_barre: depuisScan ? lu.text : null,
            format_code: depuisScan ? lu.format : null,
          }));
        } catch (err) {
          if (err?.response?.status === 409 && err.response.data?.id) fiche = (await api.get(`/colis/${err.response.data.id}`)).data;
          else throw err;
        }
      }
      let msg = existant ? `Colis ${numeroLisible(fiche.numero)}` : `Colis ${numeroLisible(fiche.numero)} créé`;
      if (avecInstance) {
        setBusy("instance");
        const r = await enregistrerInstance(fiche.id, inst, (p) => setBusy(`photos ${p}`));
        r.erreurs.forEach((e) => toast.error(errMsg(e)));
        fiche = r.fiche;
        msg += ` — instance : ${r.inst.motif_label}`;
      }
      toast.success(msg);
      onCreated?.(fiche);
      if (suite === "suivant") {
        setCompteur((c) => c + 1);
        // caméra rouverte seulement si ce colis avait été lu à la caméra (pas avec le lecteur Zebra)
        reset(!!lu && lu.format !== "Lecteur");
      } else {
        onClose();
        navigate(`/colis/${fiche.id}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(null);
    }
  };

  const instOk = instanceValide(motifs, inst);
  const pret = valide && existant !== undefined && !busy;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[94dvh] overflow-y-auto" data-testid="ajout-colis-dialog">
        <DialogHeader>
          <DialogTitle>Scanner un colis{compteur > 0 && <span className="ml-2 text-xs font-normal text-muted-foreground">({compteur} enregistré{compteur > 1 ? "s" : ""})</span>}</DialogTitle>
          <DialogDescription>
            {mode === "scan" ? "Visez l'airwaybill de l'étiquette (FedEx ou TNT) : le numéro est saisi automatiquement, les autres codes sont ignorés." : "Scannez ou saisissez l'airwaybill, puis indiquez l'instance."}
          </DialogDescription>
        </DialogHeader>

        {mode === "scan" ? (
          <BarcodeScanner onDetected={detecte} onCancel={() => setMode("saisie")} />
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="colis-numero">N° de colis (airwaybill)</Label>
              <div className="flex gap-2">
                <Input
                  id="colis-numero"
                  ref={champNumero}
                  value={saisie}
                  onChange={(e) => { const avant = saisieRef.current; setSaisie(e.target.value); rafale(avant, e.target.value); }}
                  inputMode={clavier ? "text" : "none"}
                  placeholder="ex. 878089574970 ou 1141002544774683"
                  className="font-mono text-lg h-12 tracking-wider"
                  autoComplete="off"
                  autoFocus={!lu}
                  data-testid="colis-numero"
                />
                {ECRAN_TACTILE && (
                  <Button type="button" variant={clavier ? "secondary" : "outline"} className="h-12 px-3" title="Taper le n° au clavier" data-testid="colis-numero-clavier"
                    onClick={() => { setClavier((c) => !c); setTimeout(() => { champNumero.current?.blur(); champNumero.current?.focus(); }, 30); }}>
                    <Keyboard size={18} />
                  </Button>
                )}
                <Button type="button" variant="outline" className="h-12 px-4" onClick={() => setMode("scan")} data-testid="btn-scanner">
                  <ScanLine size={18} /> Scanner
                </Button>
              </div>
              {saisie.trim() && valide && !depuisScan && (
                <p className="text-xs text-muted-foreground" data-testid="colis-type">
                  {TYPES_AWB[type].label}{numero !== saisie.replace(/\s/g, "").toUpperCase() && <> · numéro retenu : <span className="font-mono font-semibold text-foreground">{numero}</span></>}
                </p>
              )}
              {saisie.trim() && !valide && <p className="text-xs text-red-600 dark:text-red-400" data-testid="colis-invalide">Ce n'est pas un airwaybill : {AIDE_AWB}.</p>}
            </div>

            {depuisScan && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm flex gap-2" data-testid="colis-lu">
                <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p>{TYPES_AWB[infoLu.type]?.label} — lu sur l'étiquette : <span className="font-mono font-bold">{numeroLisible(numero)}</span></p>
                  {et?.expedition ? (
                    <p className="text-xs text-muted-foreground mt-0.5">N° d'expédition <span className="font-mono">{et.expedition}</span> · pièce <span className="font-mono">{et.piece}</span></p>
                  ) : et ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {et.destinataire && <span className="font-medium text-foreground">{et.destinataire}</span>}
                      {[et.adresse, [et.cp, et.ville].filter(Boolean).join(" ")].filter(Boolean).map((x) => ` · ${x}`).join("")}
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {valide && existant && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex gap-2" data-testid="colis-existant">
                <FileText size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p>Colis déjà enregistré : l'instance sera ajoutée à sa fiche.</p>
                  {existant.instance_en_cours && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><AlertOctagon size={12} /> En cours : {existant.instance_en_cours}{existant.instances_ouvertes > 1 ? ` (+${existant.instances_ouvertes - 1})` : ""}</p>
                  )}
                </div>
              </div>
            )}

            {valide && (
              <div className="rounded-xl border border-border p-3 sm:p-4 space-y-3" data-testid="ajout-instance">
                <p className="font-heading font-semibold text-sm">Instance</p>
                <InstanceForm motifs={motifs} value={inst} onChange={setInst} compact />
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose} disabled={!!busy}>Fermer</Button>
              {valide && !existant && (
                <Button type="button" variant="outline" onClick={() => enregistrer(false, "fiche")} disabled={!pret} data-testid="btn-creer-sans-instance">
                  Créer sans instance
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => enregistrer(true, "suivant")} disabled={!pret || !instOk} data-testid="btn-enregistrer-suivant">
                <ScanLine size={14} /> Enregistrer et scanner le suivant
              </Button>
              <Button type="button" onClick={() => enregistrer(true, "fiche")} disabled={!pret || !instOk} data-testid="btn-creer-colis">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <PackagePlus size={14} />}
                {busy ? (busy.startsWith("photos") ? `Envoi des ${busy}…` : "Enregistrement…") : "Enregistrer"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
